import {Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Platform} from '@angular/cdk/platform';
import {BLOCKS_CONFIG} from '../../blocks.config';
import {DatePipe} from '@angular/common';
import {AbstractControl, ControlContainer, FormControl, NgForm} from '@angular/forms';
import {SessionControllerService} from '../../../submission/src/app/session/session-controller.service';
import {PdfPartComponent} from '@blocks/pdf-part/pdf-part.component';
import {BlockComponent} from '@blocks/block/block.component';
import {ContentLocale} from '@paperlessio/sdk/api/util';
import {LocalizationService} from '@shared/i18n/localization.service';
import {BlockStore} from '@blocks/block/block.store';
import {DateInput} from '@paperlessio/sdk/api/models';

@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class DateInputComponent implements OnInit, OnDestroy, OnChanges {
  public platform = inject(Platform);
  public blocksConfig = inject(BLOCKS_CONFIG);
  public blockComponent = inject(BlockComponent, {optional: true});

  private localizationService = inject(LocalizationService);
  private blockStore = inject(BlockStore);
  private form = inject(NgForm);
  private datePipe = inject(DatePipe);
  private sessionController = inject(SessionControllerService, {optional: true});
  private pdfPartComponent = inject(PdfPartComponent, {optional: true});

  @Input() block: DateInput;
  @Input() slug: string;
  @Input() disabled = false;
  @Input() data: Date;
  @Input() noFutue = false;

  @Output() dataChange: EventEmitter<Date> = new EventEmitter<Date>();

  domId = '';
  formControl: FormControl<Date>;
  ineligible = false;

  get max() {
    return new Date().toISOString().split('T')[0];
  }

  public get isOverlay(): boolean {
    return !!this.pdfPartComponent;
  }

  ngOnInit() {
    if (this.blocksConfig.submittable) {
      this.domId = 'r' + (this.blocksConfig.editable ? 'e' : '') + (this.block.settings.editable ? 'q' : '');
      this.ineligible = this.sessionController && !this.sessionController.isParticipantAnOwnerOf(this.block.id);
    }

    this.formControl = new FormControl<Date>(this.data,
      this.block.settings.required ? (control => this._validatePresence(control)) : null, null);

    // we have to disable manually to prevent invalid coloring
    if (this.ineligible) {
      this.formControl.disable();
    }

    this.form.form.addControl(this.slug, this.formControl);

    if (this.blocksConfig.submittable && this.block.settings.today) {
      this.emit(this.date);
    }
  }

  ngOnDestroy() {
    this.form.form.removeControl(this.slug);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.data) {
      if (this.noFutue && this.data > new Date()) {
        this.data = new Date();
      }

      this.formControl?.setValue(this.data);
    }
  }

  get date(): string | null {
    if (this.block.settings.today && !this.data && !this.blocksConfig.printOptimized) {
      return this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    } else {
      try {
        return this.datePipe.transform(this.data, 'yyyy-MM-dd');
      } catch {
        return null;
      }
    }
  }

  emit(event: string) {
    if (!(this.blocksConfig.editable && this.block.settings.today && !this.blocksConfig.printOptimized)) {
      let date = new Date(event);

      if (this.noFutue && date > new Date()) {
        date = new Date();
      }

      this.data = date;
      this.dataChange.emit(date);
      this.formControl.markAsDirty();
      this.formControl.setValue(date, {emitViewToModelChange: false});
    }

    if (this.blocksConfig.editable && this.block.settings.today) {
      this.block.settings.today = false;
      this.blockStore.updateBlock(this.block);
    }
  }

  get currentContentLocale(): ContentLocale {
    return this.localizationService.currentContentLocale;
  }

  get originalContentLocale(): ContentLocale {
    return this.localizationService.originalContentLocale;
  }

  private _validatePresence(control: AbstractControl) {
    return (this.block.settings.required && control.value && new Date(control.value)?.getTime()) ? null : {required: true};
  }
}
